import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import Image from '../components/AppImage';
import Icon from '../components/AppIcon';
import FooterSection from './landing-page/components/FooterSection';
import LanguageSelector from './landing-page/components/LanguageSelector';

const GalleryPage = () => {
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const savedLanguage = localStorage.getItem('memorial-language') || 'ar';
    setCurrentLanguage(savedLanguage);

    // Listen for language changes
    const handleLanguageChange = (event) => {
      const newLanguage = event.detail?.language || 'ar';
      setCurrentLanguage(newLanguage);
    };

    window.addEventListener('languageChanged', handleLanguageChange);
    return () => window.removeEventListener('languageChanged', handleLanguageChange);
  }, []);

  const galleryContent = {
    ar: {
      title: "معرض المجتمع",
      subtitle: "لحظات من الذاكرة والأمل",
      description: "مجموعة من الصور التي تحكي قصة النضال السوري والاحتفال بالحرية المكتسبة",
      closeText: "إغلاق",
      prevText: "السابق",
      nextText: "التالي",
      backToHome: "العودة إلى الصفحة الرئيسية"
    },
    en: {
      title: "Community Gallery",
      subtitle: "Moments of Memory and Hope",
      description: "A collection of images that tell the story of Syrian struggle and celebrate the freedom achieved",
      closeText: "Close",
      prevText: "Previous",
      nextText: "Next",
      backToHome: "Back to Home"
    },
    tr: {
      title: "Toplum Galerisi",
      subtitle: "Hafıza ve Umut Anları",
      description: "Suriye mücadelesinin hikayesini anlatan ve kazanılan özgürlüğü kutlayan görsel koleksiyonu",
      closeText: "Kapat",
      prevText: "Önceki",
      nextText: "Sonraki",
      backToHome: "Ana Sayfaya Dön"
    }
  };

  const galleryImages = [
    {
      id: 1,
      src: "https://images.unsplash.com/photo-1701006283402-c4292c490658",
      alt: "Peaceful protesters holding Syrian flags and freedom banners in Damascus street",
      caption: "Peaceful demonstrations in Damascus",
      captionAr: "مظاهرات سلمية في دمشق",
      captionTr: "Şam'da barışçıl gösteriler",
      category: "protest"
    },
    {
      id: 2,
      src: "https://images.unsplash.com/photo-1674859155898-5fe33efaf092",
      alt: "Memorial ceremony with candles and flowers honoring Syrian freedom fighters",
      caption: "Memorial ceremony for the fallen heroes",
      captionAr: "حفل تذكاري للأبطال الشهداء",
      captionTr: "Düşen kahramanlar için anma töreni",
      category: "memorial"
    },
    {
      id: 3,
      src: "https://images.unsplash.com/photo-1553431605-57a2f7e4a12a",
      alt: "Community volunteers distributing aid packages to Syrian families in refugee camp",
      caption: "Community support and solidarity",
      captionAr: "الدعم المجتمعي والتضامن",
      captionTr: "Toplumsal destek ve dayanışma",
      category: "community"
    },
    {
      id: 4,
      src: "https://images.unsplash.com/photo-1518568841741-c41fb8284314",
      alt: "Syrian children drawing freedom symbols and peace messages on large canvas",
      caption: "Children's art for peace",
      captionAr: "فن الأطفال من أجل السلام",
      captionTr: "Barış için çocuk sanatı",
      category: "art"
    },
    {
      id: 5,
      src: "https://images.unsplash.com/photo-1699526491758-ca32aa5d6c6a",
      alt: "International solidarity march with diverse crowd holding Syrian freedom banners",
      caption: "International solidarity march",
      captionAr: "مسيرة التضامن الدولي",
      captionTr: "Uluslararası dayanışma yürüyüşü",
      category: "solidarity"
    },
    {
      id: 6,
      src: "https://images.unsplash.com/photo-1644718847159-79867fcc4b41",
      alt: "Reconstruction workers rebuilding Syrian homes with hope and determination",
      caption: "Rebuilding for the future",
      captionAr: "إعادة البناء للمستقبل",
      captionTr: "Gelecek için yeniden inşa",
      category: "reconstruction"
    },
    {
      id: 7,
      src: "https://images.unsplash.com/photo-1680759291238-2f4f9fac8d43",
      alt: "Medical volunteers providing healthcare services to Syrian communities",
      caption: "Healthcare heroes serving the community",
      captionAr: "أبطال الرعاية الصحية يخدمون المجتمع",
      captionTr: "Topluma hizmet eden sağlık kahramanları",
      category: "healthcare"
    },
    {
      id: 8,
      src: "https://images.unsplash.com/photo-1733689190257-f517a7dedd11",
      alt: "Educational workshop teaching Syrian history and culture to young generation",
      caption: "Education and cultural preservation",
      captionAr: "التعليم والحفاظ على الثقافة",
      captionTr: "Eğitim ve kültürel koruma",
      category: "education"
    },
    {
      id: 9,
      src: "https://images.unsplash.com/photo-1682405206422-fff43c8d687f",
      alt: "Celebration gathering with Syrian families reuniting after years of separation",
      caption: "Families reunited in freedom",
      captionAr: "الأسر تتجمع في الحرية",
      captionTr: "Özgürlükte birleşen aileler",
      category: "celebration"
    }
  ];

  const getCaption = (image) => {
    switch (currentLanguage) {
      case 'ar': return image?.captionAr;
      case 'tr': return image?.captionTr;
      default: return image?.caption;
    }
  };

  const openLightbox = (image, index) => {
    setSelectedImage(image);
    setCurrentIndex(index);
  };

  const closeLightbox = () => {
    setSelectedImage(null);
  };

  const navigateImage = (direction) => {
    const newIndex = direction === 'next' ?
      (currentIndex + 1) % galleryImages?.length :
      (currentIndex - 1 + galleryImages?.length) % galleryImages?.length;

    setCurrentIndex(newIndex);
    setSelectedImage(galleryImages?.[newIndex]);
  };

  const content = galleryContent?.[currentLanguage];

  return (
    <div className="min-h-screen bg-background">
      {/* Fixed Language Selector */}
      <div className="fixed top-4 right-4 z-50">
        <LanguageSelector />
      </div>

      <section id="community-gallery" className="py-24 lg:py-32 bg-background pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back to Home Button */}
          <div className="mb-8">
            <Link
              to="/"
              className={`inline-flex items-center gap-3 font-body text-sm text-text-secondary hover:text-primary memorial-transition ${currentLanguage === 'ar' ? 'flex-row-reverse' : ''}`}
            >
              <Icon name={currentLanguage === 'ar' ? "ArrowRight" : "ArrowLeft"} size={16} className="text-primary" />
              {content?.backToHome}
            </Link>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h1 className="font-memorial text-4xl md:text-5xl lg:text-6xl font-bold text-primary mb-4">
              {content?.title}
            </h1>
            <p className="font-body text-xl text-accent mb-6">
              {content?.subtitle}
            </p>
            <p className="font-body text-base md:text-lg text-text-secondary max-w-3xl mx-auto">
              {content?.description}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
            {galleryImages?.map((image, index) =>
              <motion.div
                key={image?.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                className="group cursor-pointer"
                onClick={() => openLightbox(image, index)}
              >
                <div className="relative aspect-square bg-muted rounded-lg overflow-hidden shadow-memorial hover:shadow-lg memorial-transition">
                  <Image
                    src={image?.src}
                    alt={image?.alt}
                    className="w-full h-full object-cover group-hover:scale-110 memorial-transition duration-500"
                  />

                  <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 memorial-transition">
                    <div className="absolute bottom-4 left-4 right-4">
                      <p className="font-body text-sm text-white font-medium">
                        {getCaption(image)}
                      </p>
                    </div>
                  </div>

                  <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 memorial-transition">
                    <div className="w-8 h-8 bg-background/90 rounded-full flex items-center justify-center">
                      <Icon name="Expand" size={16} className="text-primary" />
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </div>
        </div>

        {/* Lightbox Modal */}
        <AnimatePresence>
          {selectedImage &&
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 bg-background/95 backdrop-blur-sm flex items-center justify-center p-4"
              onClick={closeLightbox}
            >
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.8, opacity: 0 }}
                className="relative max-w-4xl max-h-full bg-card rounded-lg overflow-hidden shadow-memorial"
                onClick={(e) => e?.stopPropagation()}
              >
                {/* Close Button */}
                <button
                  onClick={closeLightbox}
                  className="absolute top-4 right-4 z-10 w-10 h-10 bg-background/90 hover:bg-background rounded-full flex items-center justify-center memorial-transition"
                >
                  <Icon name="X" size={20} className="text-text-primary" />
                </button>

                {/* Navigation Buttons */}
                <button
                  onClick={() => navigateImage('prev')}
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10 w-10 h-10 bg-background/90 hover:bg-background rounded-full flex items-center justify-center memorial-transition"
                >
                  <Icon name="ChevronLeft" size={20} className="text-text-primary" />
                </button>

                <button
                  onClick={() => navigateImage('next')}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 z-10 w-10 h-10 bg-background/90 hover:bg-background rounded-full flex items-center justify-center memorial-transition"
                >
                  <Icon name="ChevronRight" size={20} className="text-text-primary" />
                </button>

                {/* Image */}
                <div className="aspect-video max-h-[70vh] bg-muted">
                  <Image
                    src={selectedImage?.src}
                    alt={selectedImage?.alt}
                    className="w-full h-full object-contain"
                  />
                </div>

                {/* Caption */}
                <div className="p-6">
                  <h3 className="font-body text-lg font-semibold text-primary mb-2">
                    {getCaption(selectedImage)}
                  </h3>
                  <div className="flex items-center justify-between text-sm text-text-secondary">
                    <span>
                      {currentIndex + 1} / {galleryImages?.length}
                    </span>
                    <span className="capitalize">
                      {selectedImage?.category}
                    </span>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          }
        </AnimatePresence>
      </section>

      <FooterSection />
    </div>
  );
};

export default GalleryPage;

